<template>
  <div v-if="friendProfile && friendProfile.friendInfo && friendProfile.friendInfo.standardProfile" class="container">
    <div class="profileidentstandardvolunteerform">
      <div class="row">
        <div class="col-12">
          <h2>{{ $t('volunteerForm.updateTitle') }}</h2>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <backbar />
        </div>
      </div>
      <div class="row">
        <div class="col-12 text-center py-2">
          <validation :id="'validationTarget' + _uid"
                      ref="validationtarget"
                      :validation-errors="validationErrors"
                      exclude-field-reference
                      include-title
          />
        </div>
      </div>
      <div class="row feature-panel">
        <div class="col-12 col-sm-5 col-md-4">
          <forminput
            v-model="friendProfile.phoneNumber"
            v-validate="'required'"
            :hint="$t('volunteerForm.phone')"
            :is-valid="!errors.has('volunteerForm.phone')"
            name="volunteerForm.phone"
            :required="true"
            disabled
            :tooltip="$t('volunteerForm.disabledPhone')"
          />
        </div>
      </div>
      <div class="row feature-panel">
        <div class="col-12">
          <VolunteerProfileCommon :volunteer="friendProfile.friendInfo" @input="onVolunteerChanged" />
        </div>
      </div>
      <div class="row feature-panel">
        <div class="col-12">
          <ProfileInfoForm :profile="friendProfile" :is-net-profile="false" @input="onFriendProfileChanged" />
        </div>
      </div>
      <div class="row feature-panel">
        <div class="col-12">
          <VolunteerProfileStandard :volunteer="friendProfile.friendInfo.standardProfile" @input="onVolunteerStandardProfileChanged" />
        </div>
      </div>
      <div class="row feature-panel">
        <div class="col-12 sectionheader">
          <h3>{{ $t("nonIdentProfileForm.applicationState") }}</h3>
        </div>
        <div class="col-12">
          <InterviewForm
            v-if="friendProfile"
            :value="computedInterviewData"
            :disabled="isStateChangeDisabled"
            @input="onInterviewDataChanged"
          />
        </div>
        <div class="col-12">
          <OnHoldForm
            :value="computedOnHoldData"
            :disabled="isStateChangeDisabled"
            @input="onOnHoldDataChanged"
          />
        </div>
        <div class="col-12">
          <LastContactedForm
            v-if="friendProfile.friendInfo"
            :value="friendProfile.friendInfo.lastContacted"
            @input="onlastContactedChanged"
          />
        </div>
      </div>
      <div class="row feature-panel">
        <div class="col-12">
          <textareainput
            v-model="friendProfile.friendInfo.standardProfile.privateNotes"
            :title="$t('volunteerForm.privateNotes')" :rows="6"
          />
        </div>
      </div>
      <div class="row feature-panel buttons">
        <div class="col-6 cancel">
          <SprButton type="button"
                     :size="1"
                     :title="$t('volunteerForm.cancel')"
                     click-event="onCancel"
                     @onCancel="cancel"
          />
        </div>
        <div class="col-6 save">
          <SprButton type="button"
                     :size="1"
                     :title="$t('volunteerForm.save')"
                     click-event="onSave"
                     @onSave="save"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue'
  import { LocaleMessage } from 'vue-i18n'
  import SprButton from '@/components/SPRButton.vue'
  import forminput from '../SPRSimpleText.vue'
  import ProfileInfoForm from '@/components/friends/ProfileInfoForm.vue'
  import VolunteerProfileCommon from '@/components/friends/VolunteerProfileCommon.vue'
  import VolunteerProfileStandard from '@/components/friends/VolunteerProfileStandard.vue'
  import InterviewForm from '@/components/friends/InterviewForm.vue'
  import OnHoldForm from '@/components/friends/OnHoldForm.vue'
  import LastContactedForm from '@/components/friends/LastContactedForm.vue'
  import textareainput from '../SPRSimpleTextArea.vue'
  import backbar from '../BackBar.vue'
  import validation from '../SPRValidationError.vue'
  import Alerts from '@/mixins/alerts.js'
  import { ErrorBag, FieldError } from 'vee-validate'
  import { UserProfileDto, FriendProfileDto, FriendVolunteerDto, FriendVolunteerProfileStandardDto, GetFriendProfileByFriendVolunteerIdCriteria, CustomerState } from '@/types/index'
  import { InterviewData, OnHoldData } from '@/types/custom'
  import { convertToUTC } from '@/helpers/datehelper'

  // the data object
  export interface TemplateComponentData {
    friendProfile: FriendProfileDto | undefined
  }

  // This is the component for managing updating an ident users Standard Friend profile.
  export default Vue.extend({
    name: 'ProfileIdentStandardVolunteerForm',
    components: { validation, backbar, forminput, ProfileInfoForm, VolunteerProfileCommon, VolunteerProfileStandard, InterviewForm, OnHoldForm, SprButton, textareainput, LastContactedForm },
    mixins: [Alerts],
    props: {
      profileId: {
        type: Number,
        default: 0
      },
      groupId: {
        type: Number,
        required: true
      }
    },
    data (): TemplateComponentData {
      return {
        friendProfile: undefined
      }
    },
    computed: {
      validationErrors (): Array<Array<string>> {
        var errs: ErrorBag = this.errors ? this.errors : []
        if (errs.items.length) {
          return errs.items.map((err: FieldError) => {
            return [`error_${err.field}`, err.rule ? err.rule : '', err.msg ? err.msg : '']
          })
        }
        return []
      },
      computedInterviewData (): InterviewData | null {
        if (!this.friendProfile?.friendInfo?.standardProfile) {
          return {
            interviewed: false,
            interviewedBy: '',
            interviewedDate: undefined
        } as InterviewData
        }

        var defaultDate = new Date() // must be in UTC date format
        const p = this.friendProfile.friendInfo.standardProfile
        
        return {
          interviewed: p.volunteerInterviewed,
          interviewedBy: p.volunteerInterviewedBy,
          interviewedDate: p.volunteerInterviewedOnDate || defaultDate
        } as InterviewData
      },
      computedOnHoldData (): OnHoldData | null {
        if (this.friendProfile?.friendInfo?.standardProfile) {
          const p = this.friendProfile.friendInfo.standardProfile
          return {
            onHold: Boolean(p.onHoldDeadline),
            onHoldDeadline: p.onHoldDeadline,
            onHoldDetails: p.onHoldDetails
          } as OnHoldData
        }

        return null
      },
      isStateChangeDisabled (): boolean {
        /// Cannot change the state if the customer has friendships
        return this.friendProfile?.friendInfo.standardProfile.state === CustomerState.PendingFriend ||
          this.friendProfile?.friendInfo.standardProfile.state === CustomerState.ActiveFriendship ||
          Boolean(this.friendProfile?.friendInfo.standardProfile.friendships?.length)
      }
    },
    mounted (): void {
      var criteria: GetFriendProfileByFriendVolunteerIdCriteria = {
        friendManagementGroupId: this.groupId,
        friendVolunteerId: this.profileId,
        includeFriendships: false,
        includeFriendPools: false
      }
      var url = (this.$store.state.apiBases.friendservice + this.$store.state.apiEndpoints.volunteergetstandardprofilebyid[0])
      this.$http.post(url, criteria).then(s => {
        if (s.ok) {
          // @ts-ignore
          this.friendProfile = s.body as FriendProfileDto
          return
        }
        this.failLoad()
      }, f => this.failLoad())
    },
    provide (): Record<string, any> {
      return {
        $validator: this.$validator // allow child components to have validator injected
      }
    },
    methods: {
      failLoad (): void {
        this.displayError(this.$t('common.fetchFailed'))
      },
      focusOnErrors () {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
      onFriendProfileChanged (newFriendProfile: FriendProfileDto): void {
        this.friendProfile = newFriendProfile
      },
      onVolunteerChanged (newVolunteer: FriendVolunteerDto): void {
        if (this.friendProfile) {
          this.friendProfile.friendInfo = newVolunteer
        }
      },
      onVolunteerStandardProfileChanged (newProfile: FriendVolunteerProfileStandardDto): void {
        if (this.friendProfile) {
          this.friendProfile.friendInfo.standardProfile = newProfile
        }
      },
      onInterviewDataChanged (newData: InterviewData): void {
        if (this.friendProfile) {
          this.friendProfile.friendInfo.standardProfile.volunteerInterviewed = newData.interviewed
          this.friendProfile.friendInfo.standardProfile.volunteerInterviewedBy = newData.interviewedBy
          this.friendProfile.friendInfo.standardProfile.volunteerInterviewedOnDate = newData.interviewedDate
        }
      },
      onOnHoldDataChanged (newData: OnHoldData): void {
        if (this.friendProfile) {
          this.friendProfile.friendInfo.standardProfile.onHoldDeadline = newData.onHold ? newData.onHoldDeadline : undefined
          this.friendProfile.friendInfo.standardProfile.onHoldDetails = newData.onHold ? newData.onHoldDetails : ''
        }
      },
      onlastContactedChanged (hasContactDate: boolean, lastContacted: Date): void {
        if (!this.friendProfile?.friendInfo) return

        this.friendProfile.friendInfo.lastContacted = hasContactDate ? lastContacted : undefined
      },
      cancel (): void {
        const url = `/friend/volunteerpreview/${this.groupId}/${this.profileId}`
        this.$router.push(url)
      },
      save (): void {
        if (this.friendProfile?.friendInfo?.standardProfile?.preferredAges === 0) {
          this.friendProfile.friendInfo.standardProfile.preferredAges = 1
          this.$nextTick(() => {
            if (!this.friendProfile) return
            this.friendProfile.friendInfo.standardProfile.preferredAges = 0
            this.validateAndSave()
          })
        } else {
          this.validateAndSave()
        }
      },
      validateAndSave (): void {
        this.$validator.validateAll().then(result => {
          // check any child controls have not added errors
          if (!result || (this.validationErrors && this.validationErrors.length)) {
            var target = this.$refs['validationtarget']
            if (target) {
              window.scrollTo({
                // @ts-ignore
                top: target.$el.parentElement.clientHeight,
                left: 0,
                behavior: 'smooth'
              })
            }
            return
          }
          if (this.friendProfile) {
            const data = this.friendProfile
            // @ts-ignore
            data.friendInfo.netProfile = null
            this.updateFriendProfile(data)
          }
        })
      },
      updateFriendProfile (data: FriendProfileDto): void {
        data.friendInfo = this.convertDatesToUTC(data.friendInfo)

        const updateUrl = this.$store.state.apiBases.friendservice + this.$store.state.apiEndpoints.updateidentstandardprofile[0]

        this.$http.put(updateUrl, data).then((result: any) => {
          this.$router.push(`/friend/volunteerpreview/${this.groupId}/${this.profileId}`)
        }, (err: Response | any) => {
          this.displayError(err)
        })
      },
      convertDatesToUTC (friendInfo: FriendVolunteerDto): FriendVolunteerDto {
        // convert dates to UTC and make sure they are corrected to the right date
        if (friendInfo?.lastContacted) {
          friendInfo.lastContacted = convertToUTC(friendInfo?.lastContacted)
        }
        if (friendInfo?.standardProfile?.volunteerInterviewed && friendInfo?.standardProfile?.volunteerInterviewedOnDate) {
          friendInfo.standardProfile.volunteerInterviewedOnDate = convertToUTC(friendInfo.standardProfile.volunteerInterviewedOnDate)
        }
        if (friendInfo?.standardProfile?.onHoldDeadline) {
          friendInfo.standardProfile.onHoldDeadline = convertToUTC(friendInfo?.standardProfile.onHoldDeadline)
        }

        return friendInfo
      },
      displayError (err: Response | any): void {
        if (Array.isArray(err.body)) {
          err.body.forEach(x => {
            // @ts-ignore
            this.showAlertError(x.errorMessage)
          })
        } else if (err.body && err.body.errorMessage) {
          // @ts-ignore
          this.showAlertError(err.errorMessage)
        } else if (err.bodyText) {
          // @ts-ignore
          this.showAlertError(err.bodyText)
        } else {
          // @ts-ignore
          this.showAlertError(this.$t('common.fetchFailed'))
        }
      }
    }
  })
</script>

<style lang="scss">
  @import "../../assets/scss/_variables.scss";
  .profileidentstandardvolunteerform {
    .feature-panel {
      background-color: $brand-white;
      margin-bottom: 30px;
      padding-top: 15px;

      &.buttons {
        margin-top: 30px;
        padding-top: 15px;
        padding-bottom: 15px;
        .cancel {
          text-align: left;
        }
        .save {
          text-align: right;
        }
      }
    }

    .profilehead {
      margin-top: 25px;
      margin-bottom: 0;
      padding-top: 10px;
      padding-bottom: 10px;
      background-color: $brand-white;
    }

    .subheader {
      margin-top: 15px;
      margin-bottom: 15px;
      display: block;
    }

    .profile-button {
      margin-top: 20px;
      display: flex;
      justify-content: center;
    }

    .fa-exclamation-triangle {
      color: $brand-red;
    }
  }
</style>
